import _get from 'lodash/get'
import { match } from 'ts-pattern'
import * as z from 'zod'

import { webBreedElements } from '@flows/configs/petHealthV2/steps/elements/breeds'
import { petBirthdayElements } from '@flows/configs/petHealthV2/steps/elements/petBirthday'
import { petGenderElements } from '@flows/configs/petHealthV2/steps/elements/petGender'
import { petNameElements } from '@flows/configs/petHealthV2/steps/elements/petName'
import { petTypeElements } from '@flows/configs/petHealthV2/steps/elements/petType'
import { postalCodeElements } from '@flows/configs/petHealthV2/steps/elements/postalCode'
import { v2Text } from '@flows/configs/petHealthV2/steps/elements/shared'
import {
  copyCustomerZipCode,
  fetchBreedOptions,
  hasCustomerZipCode,
  isBreedOfferedForSpecies,
  isMissingBreed,
  nextStepAfterBreeds,
  postalCodeErrorMap,
  submitBreeds,
  validateZipCode,
} from '@flows/configs/petHealthV2/utils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import {
  type ElementFragment,
  combineFragments,
  fragmentStep,
} from '@flows/configs/shared/stepFragments'
import type { StepConfigParams } from '@flows/configs/types'
import { t } from '@flows/core/i18n'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'

const sectionSpacer = (index: number) =>
  ({ type: 'spacer', size: index === 0 ? 'none' : 'gutter' }) as const

const toWebSectionElement = (element: AnyStepContentElement): AnyStepContentElement =>
  match(element)
    .with({ type: 'title' }, (title) => ({ ...title, size: 'h4' as const, isSmall: true }))
    .with({ type: 'codeInput' }, (code) => ({ ...code, align: 'start' as const }))
    .otherwise((other) => other)

const webSections = (fragments: readonly ElementFragment[]) =>
  fragments.map(({ elements, schema }, index): ElementFragment => {
    const [first] = elements
    return {
      elements: [
        {
          ...sectionSpacer(index),
          ...(first && 'condition' in first && { condition: first.condition }),
        },
        ...elements.map(toWebSectionElement),
      ],
      schema,
    }
  })

const askedUnlessCustomerZipCode = ({ elements, schema }: ElementFragment): ElementFragment => ({
  elements: elements.map((element) => ({
    ...element,
    condition: {
      type: 'function',
      valuePaths: ['zip_code'],
      function: (values) => !hasCustomerZipCode(values ?? {}),
    },
  })),
  schema,
})

const breedsBySpecies = (params: StepConfigParams): ElementFragment => ({
  elements: [],
  schema: schemaUtils
    .generateZodObjectFromPaths({
      [params.helperFunctions.getTransientPath('breeds_by_species')]: z
        .record(z.array(z.string()))
        .optional(),
    })
    .partial(),
})

const flagBreedsOfOtherSpecies = <TStep extends { schema: ElementFragment['schema'] }>(
  params: StepConfigParams,
  step: TStep
) => ({
  ...step,
  schema: step.schema.superRefine((values, ctx) =>
    (['first_breed_name', 'second_breed_name'] as const)
      .map((name) => params.helperFunctions.getParamPath(name))
      .forEach((path) => {
        const breed = _get(values, path)
        if (
          typeof breed === 'string' &&
          !isMissingBreed(breed) &&
          !isBreedOfferedForSpecies(params, values, breed)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('sharedFlows_common:generic.errors.required'),
            path: [path],
          })
        }
      })
  ),
})

const pet_details = (params: StepConfigParams) => {
  const { getParamPath } = params.helperFunctions
  const submitDerivedBreeds = submitBreeds(params, 'derive', [
    getParamPath('first_breed_name'),
    getParamPath('second_breed_name'),
  ])

  return flagBreedsOfOtherSpecies(params, {
    ...fragmentStep(
      combineFragments(
        {
          elements: [
            {
              type: 'title',
              text: v2Text('steps.pet_details.title'),
              size: 'h2',
              fontType: 'primary',
              isSmall: true,
            },
          ],
          schema: z.object({}),
        },
        ...webSections([
          petNameElements(params),
          petTypeElements(params),
          petBirthdayElements(params),
          webBreedElements(params),
          petGenderElements(params),
          askedUnlessCustomerZipCode(postalCodeElements(params, 'modal')),
        ]),
        breedsBySpecies(params)
      ),
      params,
      nextStepAfterBreeds('web', params)
    ),
    errorMap: postalCodeErrorMap,
    onPrepare: {
      type: 'simpleHook',
      callback: async (flowValues: FlowValues) => {
        const breeds = await fetchBreedOptions(params, { withImages: false }).callback(flowValues)
        return {
          formValues: {
            ...breeds.formValues,
            ...copyCustomerZipCode(params, flowValues).formValues,
          },
        }
      },
    },
    onSubmit: {
      type: 'simpleHook',
      callback: async (flowValues: FlowValues) =>
        (await validateZipCode(params)(flowValues)) ?? submitDerivedBreeds.callback(flowValues),
    },
  } as const satisfies AnyStepConfiguration)
}

export default pet_details
